import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useGamification } from '../contexts/GamificationContext';
import { supabase } from '../../lib/supabaseClient';
import {
  Droplets, Utensils, Moon, Dumbbell, Target, Plus, Trash2,
  CheckCircle2, Circle, Edit3, Lightbulb
} from 'lucide-react';
import { calculateDailyPoints, isMealsCompleted, isSleepCompleted } from '../utils/dailyProgressPoints';

type CustomFreq = 'daily' | 'weekly' | 'monthly';

interface CustomGoal {
  id: string;
  name: string;
  description?: string;
  frequency: CustomFreq;
  completed: boolean;
  periodKey: string;
  createdAt: number;
}

interface DailyProgress {
  water_ml: number;
  meals_logged: number;
  sleep_hours: number;
  workout_completed: boolean;
}

const BG_DARK = '#0D0D0D';
const BORDER_COLOR = 'rgba(255, 255, 255, 0.1)';
const ACCENT_RED = '#E53935';
const STORAGE_KEY_CUSTOM = 'shapex:custom_goals:v1';

export default function DailyGoals({ onGoWorkouts }: { onGoWorkouts?: () => void }) {
  const { user } = useAuth();
  const { addXP } = useGamification();

  // State
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState<DailyProgress>({
    water_ml: 0, meals_logged: 0, sleep_hours: 0, workout_completed: false
  });
  const [customGoals, setCustomGoals] = useState<CustomGoal[]>([]);

  // UI State
  const [snack, setSnack] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState<string | null>(null);
  const [goalCompletedMessage, setGoalCompletedMessage] = useState<Record<string, string | null>>({});

  // Form State
  const [newGoalName, setNewGoalName] = useState('');
  const [newGoalDesc, setNewGoalDesc] = useState('');
  const [newGoalFreq, setNewGoalFreq] = useState<CustomFreq>('daily');

  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editFreq, setEditFreq] = useState<CustomFreq>('daily');

  // Targets (hardcoded or from profile?)
  const targetWaterMl = 3000;
  const targetMeals = 6;
  const targetSleepH = 8;
  const trainingDaysWeek = 5;

  // Helpers
  const getPeriodKey = (freq: CustomFreq, date: Date) => {
    if (freq === 'daily') return date.toISOString().slice(0, 10);
    if (freq === 'weekly') {
      const d = new Date(date);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday start or Sunday? Using standard Sunday=0
      d.setDate(diff);
      return `week-${d.toISOString().slice(0, 10)}`;
    }
    return `month-${date.toISOString().slice(0, 7)}`;
  };

  // Load Data
  useEffect(() => {
    if (!user) return;

    const load = async () => {
      setLoading(true);
      try {
        // Load Daily Progress
        const today = new Date().toISOString().slice(0, 10);
        
        // Verificar se já temos dados do dia atual no estado local
        // Só recarregar se não tivermos dados ou se for um novo dia
        const lastLoadDateKey = `shapex_last_load_date_${user.id}`;
        const lastProgressKey = `shapex_last_progress_${user.id}`;
        const lastLoadDate = localStorage.getItem(lastLoadDateKey);
        
        // Se já carregamos hoje e temos progresso salvo, usar o estado salvo
        if (lastLoadDate === today) {
          const savedProgress = localStorage.getItem(lastProgressKey);
          if (savedProgress) {
            try {
              const parsed = JSON.parse(savedProgress);
              setProgress(parsed);
              setLoading(false);
              return; // Não recarregar do banco se já temos dados do dia atual
            } catch (e) {
              console.error('Erro ao parsear progresso salvo:', e);
            }
          }
        }
        
        // Carregar do banco (primeira vez hoje ou dados não encontrados)
        const { data: prog } = await supabase
          .from('daily_progress')
          .select('*')
          .eq('user_id', user.id)
          .eq('date', today)
          .maybeSingle();

        const progressData = prog
          ? {
              water_ml: prog.water_ml || 0,
              meals_logged: prog.meals_logged || 0,
              sleep_hours: prog.sleep_hours || 0,
              workout_completed: prog.workout_completed || false
            }
          : {
              water_ml: 0,
              meals_logged: 0,
              sleep_hours: 0,
              workout_completed: false
            };
        
        setProgress(progressData);
        
        // Salvar no localStorage para persistência entre navegações
        localStorage.setItem(lastLoadDateKey, today);
        localStorage.setItem(lastProgressKey, JSON.stringify(progressData));

        // Load Custom Goals
        const raw = localStorage.getItem(STORAGE_KEY_CUSTOM);
        if (raw) {
          let parsed: CustomGoal[] = JSON.parse(raw);
          // Reset logic based on periodKey
          const d = new Date();
          parsed = parsed.map(g => {
            const currentKey = getPeriodKey(g.frequency, d);
            if (g.periodKey !== currentKey) {
              return { ...g, completed: false, periodKey: currentKey };
            }
            return g;
          });
          setCustomGoals(parsed);
          localStorage.setItem(STORAGE_KEY_CUSTOM, JSON.stringify(parsed));
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  // Upsert Progress
  const upsertProgress = async (update: Partial<DailyProgress>) => {
    if (!user) return;

    // Usar atualização funcional para evitar race conditions
    let oldProgressForXP: DailyProgress | null = null;
    
    setProgress((prev) => {
      // Salvar estado anterior para cálculo de XP
      oldProgressForXP = { ...prev };
      
      // Aplicar update preservando valores existentes usando estado mais recente
      const newProgress: DailyProgress = {
        water_ml: update.water_ml !== undefined ? update.water_ml : prev.water_ml || 0,
        meals_logged: update.meals_logged !== undefined ? update.meals_logged : prev.meals_logged || 0,
        sleep_hours: update.sleep_hours !== undefined ? update.sleep_hours : prev.sleep_hours || 0,
        workout_completed: update.workout_completed !== undefined ? update.workout_completed : prev.workout_completed || false
      };

      // XP Logic usando estado anterior salvo
      if (oldProgressForXP) {
        // Meals
        if (update.meals_logged !== undefined) {
          const diff = update.meals_logged - oldProgressForXP.meals_logged;
          if (diff > 0) {
            addXP(diff * 5);
          }
        }
        // Sleep
        if (update.sleep_hours !== undefined) {
          if (update.sleep_hours >= targetSleepH && oldProgressForXP.sleep_hours < targetSleepH) {
            addXP(5);
            setGoalCompletedMessage(prev => ({ ...prev, sleep: 'Meta concluída!' }));
            setTimeout(() => setGoalCompletedMessage(prev => ({ ...prev, sleep: null })), 3000);
          }
        }
        // Workout
        if (update.workout_completed === true && !oldProgressForXP.workout_completed) {
          addXP(10);
          setGoalCompletedMessage(prev => ({ ...prev, workout: 'Meta concluída!' }));
          setTimeout(() => setGoalCompletedMessage(prev => ({ ...prev, workout: null })), 3000);
        }
        // Water - check if reached target
        if (update.water_ml !== undefined && oldProgressForXP) {
          if (update.water_ml >= targetWaterMl && oldProgressForXP.water_ml < targetWaterMl) {
            addXP(5);
            setGoalCompletedMessage(prev => ({ ...prev, water: 'Meta concluída!' }));
            setTimeout(() => setGoalCompletedMessage(prev => ({ ...prev, water: null })), 3000);
          }
        }
        // Meals - check if reached target
        if (update.meals_logged !== undefined && oldProgressForXP) {
          if (update.meals_logged >= targetMeals && oldProgressForXP.meals_logged < targetMeals) {
            addXP(5);
            setGoalCompletedMessage(prev => ({ ...prev, meals: 'Meta concluída!' }));
            setTimeout(() => setGoalCompletedMessage(prev => ({ ...prev, meals: null })), 3000);
          }
        }
      }

      // Salvar no localStorage imediatamente para persistência entre navegações
      const today = new Date().toISOString().slice(0, 10);
      localStorage.setItem(`shapex_last_load_date_${user.id}`, today);
      localStorage.setItem(`shapex_last_progress_${user.id}`, JSON.stringify(newProgress));
      
      // Salvar no banco de forma assíncrona
      // Buscar dados existentes para preservar campos não atualizados
      supabase
        .from('daily_progress')
        .select('*')
        .eq('user_id', user.id)
        .eq('date', today)
        .maybeSingle()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then(({ data: existing }: { data: any }) => {
          // Calcular pontos e flags de conclusão
          const mealsCompleted = isMealsCompleted(newProgress.meals_logged);
          const sleepCompleted = isSleepCompleted(newProgress.sleep_hours);
          const points = calculateDailyPoints({
            workout_completed: newProgress.workout_completed,
            sleep_hours: newProgress.sleep_hours,
            meals_logged: newProgress.meals_logged
          });

          console.log('DailyGoals - Calculando pontos:', {
            workout_completed: newProgress.workout_completed,
            sleep_hours: newProgress.sleep_hours,
            meals_logged: newProgress.meals_logged,
            mealsCompleted,
            sleepCompleted,
            points
          });

          const finalProgress = existing
            ? {
                ...existing,
                water_ml: newProgress.water_ml,
                meals_logged: newProgress.meals_logged,
                sleep_hours: newProgress.sleep_hours,
                workout_completed: newProgress.workout_completed,
                meals_completed: mealsCompleted,
                sleep_completed: sleepCompleted,
                points: points
              }
            : {
                user_id: user.id,
                date: today,
                water_ml: newProgress.water_ml,
                meals_logged: newProgress.meals_logged,
                sleep_hours: newProgress.sleep_hours,
                workout_completed: newProgress.workout_completed,
                meals_completed: mealsCompleted,
                sleep_completed: sleepCompleted,
                points: points
              };

          console.log('DailyGoals - Salvando progresso:', finalProgress);

          return supabase
            .from('daily_progress')
            .upsert(finalProgress, { onConflict: 'user_id,date' });
        })
        .then(({ data, error }: { data: any; error: Error | null }) => {
          if (error) {
            console.error('Erro ao salvar progresso:', error);
            console.error('Detalhes do erro:', {
              message: error.message,
              code: (error as any).code,
              details: (error as any).details,
              hint: (error as any).hint
            });
            // Em caso de erro, tentar salvar novamente
            const today = new Date().toISOString().slice(0, 10);
            const mealsCompleted = isMealsCompleted(newProgress.meals_logged);
            const sleepCompleted = isSleepCompleted(newProgress.sleep_hours);
            const points = calculateDailyPoints({
              workout_completed: newProgress.workout_completed,
              sleep_hours: newProgress.sleep_hours,
              meals_logged: newProgress.meals_logged
            });
            
            supabase
              .from('daily_progress')
              .upsert({
                user_id: user.id,
                date: today,
                water_ml: newProgress.water_ml,
                meals_logged: newProgress.meals_logged,
                sleep_hours: newProgress.sleep_hours,
                workout_completed: newProgress.workout_completed,
                meals_completed: mealsCompleted,
                sleep_completed: sleepCompleted,
                points: points
              }, { onConflict: 'user_id,date' })
              .then(({ error: retryError }: { error: Error | null }) => {
                if (!retryError) {
                  window.dispatchEvent(new CustomEvent('dailyProgressUpdated'));
                }
              });
          } else {
            // Disparar evento para atualizar dashboard
            window.dispatchEvent(new CustomEvent('dailyProgressUpdated'));
          }
        });
      
      return newProgress;
    });
  };

  // Custom Goals Handlers
  const persistCustom = (goals: CustomGoal[]) => {
    setCustomGoals(goals);
    localStorage.setItem(STORAGE_KEY_CUSTOM, JSON.stringify(goals));
  };

  const handleAddCustom = () => {
    if (!newGoalName.trim()) return;
    const d = new Date();
    const newGoal: CustomGoal = {
      id: crypto.randomUUID(),
      name: newGoalName,
      description: newGoalDesc,
      frequency: newGoalFreq,
      completed: false,
      periodKey: getPeriodKey(newGoalFreq, d),
      createdAt: Date.now()
    };
    persistCustom([...customGoals, newGoal]);
    setNewGoalName('');
    setNewGoalDesc('');
    setShowAddForm(false);
    setSnack('Meta criada com sucesso!');
    setTimeout(() => setSnack(''), 3000);
  };

  const handleToggleCustomCompleted = (id: string, completed: boolean) => {
    const next = customGoals.map(g => {
      if (g.id !== id) return g;
      return { ...g, completed };
    });
    persistCustom(next);
    if (completed) {
      addXP(15);
      setSnack('Meta concluída! +15 XP');
    }
    setTimeout(() => setSnack(''), 3000);
  };

  const handleDeleteCustom = (id: string) => {
    persistCustom(customGoals.filter(g => g.id !== id));
  };

  const startEditCustom = (g: CustomGoal) => {
    setEditingGoalId(g.id);
    setEditName(g.name);
    setEditDesc(g.description || '');
    setEditFreq(g.frequency);
  };

  const saveEditCustom = () => {
    if (!editName.trim()) return;
    const d = new Date();
    const next = customGoals.map(g => {
      if (g.id !== editingGoalId) return g;
      const newKey = getPeriodKey(editFreq, d);
      const changedFreq = editFreq !== g.frequency;
      return {
        ...g,
        name: editName,
        description: editDesc.trim() || undefined,
        frequency: editFreq,
        periodKey: newKey,
        completed: changedFreq ? false : g.completed
      };
    });
    persistCustom(next);
    setEditingGoalId(null);
    setSnack('Meta atualizada.');
    setTimeout(() => setSnack(''), 3000);
  };

  // Calculations for UI
  const dayProgressPct = Math.round(
    ((Math.min(progress.water_ml, targetWaterMl) / targetWaterMl) * 0.25 +
      (Math.min(progress.meals_logged, targetMeals) / targetMeals) * 0.25 +
      (Math.min(progress.sleep_hours, targetSleepH) / targetSleepH) * 0.25 +
      (progress.workout_completed ? 0.25 : 0)) * 100
  );

  const dailyMindset = "A disciplina é a ponte entre metas e realizações.";
  const resetBanner = null;

  const glowWater = progress.water_ml >= targetWaterMl;
  const glowMeals = progress.meals_logged >= targetMeals;
  const glowSleep = progress.sleep_hours >= targetSleepH;
  const glowWorkout = progress.workout_completed;

  const currentTip = "Beba água ao acordar para ativar o metabolismo.";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pb-20" style={{ backgroundColor: BG_DARK }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E53935]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: BG_DARK }}>
      <div className="max-w-md mx-auto px-4 pt-6">
        <header className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Metas</h1>
            <p className="text-[#A1A1A1] text-xs">Sua rotina de alta performance</p>
            <p className="text-[#A1A1A1] text-xs opacity-70 mt-2">
              O XP mostra sua evolução diária. Cada meta concluída soma pontos que refletem sua consistência.
            </p>
          </div>
        </header>

        {resetBanner && (
          <div className="mb-4 p-3 rounded-xl bg-[#E53935]/10 border border-[#E53935]/20 text-[#E53935] text-xs font-medium flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            {resetBanner}
          </div>
        )}

        {/* Progresso do dia */}
        <div className="mb-6 bg-[#141414] rounded-[18px] p-5 border shadow-lg" style={{ borderColor: BORDER_COLOR }}>
          <div className="flex justify-between items-end mb-2">
            <span className="text-white font-bold text-sm">Progresso Diário</span>
            <span className="text-[#E53935] font-bold text-sm">{dayProgressPct}%</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden bg-[#1A1A1A]">
            <div
              className="h-full transition-all duration-700 ease-out rounded-full"
              style={{
                width: `${dayProgressPct}%`,
                backgroundColor: ACCENT_RED,
                boxShadow: dayProgressPct >= 100 ? '0 0 10px rgba(229, 57, 53, 0.5)' : undefined,
              }}
            />
          </div>
          <p className="text-[#A1A1A1] italic text-xs mt-4 border-t border-white/5 pt-3">"{dailyMindset}"</p>
        </div>

        {/* Cards de metas */}
        <div className="space-y-4">
          {/* Água */}
          <div className="bg-[#141414] rounded-[18px] p-5 border shadow-lg transition-all duration-300" style={{ borderColor: glowWater ? ACCENT_RED : BORDER_COLOR }}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(229, 57, 53, 0.1)' }}>
                  <Droplets className="w-5 h-5 text-[#E53935]" />
                </div>
                <div>
                  <div className="text-white font-bold text-sm">Hidratação</div>
                  <div className="text-[#A1A1A1] text-xs">Meta: {(targetWaterMl / 1000).toFixed(1)}L</div>
                  <div className="text-[#E53935] text-[10px] mt-0.5">+5 XP ao atingir meta</div>
                </div>
              </div>
              <div className="text-right">
                <span className="text-white font-bold text-lg">{(progress.water_ml / 1000).toFixed(1)}</span>
                <span className="text-[#A1A1A1] text-xs">L</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                className="flex-1 py-2 rounded-lg border border-white/10 text-[#A1A1A1] text-xs hover:bg-white/5 hover:text-white transition-colors"
                onClick={() => upsertProgress({ water_ml: Math.max(0, (progress.water_ml || 0) - 250) })}
              >
                -250ml
              </button>
              <button
                className="flex-[2] py-2 rounded-lg bg-[#E53935]/10 border border-[#E53935]/30 text-[#E53935] text-xs font-bold hover:bg-[#E53935]/20 transition-colors"
                onClick={() => upsertProgress({ water_ml: Math.min(targetWaterMl, (progress.water_ml || 0) + 250) })}
              >
                +250ml
              </button>
            </div>
            {goalCompletedMessage.water && (
              <div className="mt-3 p-2 rounded-lg bg-[#E53935]/10 border border-[#E53935]/20 text-[#E53935] text-xs font-medium flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3" />
                {goalCompletedMessage.water}
              </div>
            )}
          </div>

          {/* Refeições */}
          <div className="bg-[#141414] rounded-[18px] p-5 border shadow-lg transition-all duration-300" style={{ borderColor: glowMeals ? ACCENT_RED : BORDER_COLOR }}>
            <div className="flex items-start justify-between mb-4">

              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(229, 57, 53, 0.1)' }}>
                  <Utensils className="w-5 h-5 text-[#E53935]" />
                </div>
                <div>
                  <div className="text-white font-bold text-sm">Refeições</div>
                  <div className="text-[#A1A1A1] text-xs">Meta: {targetMeals} ref</div>
                  <div className="text-[#E53935] text-[10px] mt-0.5">+5 XP por refeição</div>
                </div>
              </div>
              <div className="text-right">
                <span className="text-white font-bold text-lg">{progress.meals_logged}</span>
                <span className="text-[#A1A1A1] text-xs">/{targetMeals}</span>
              </div>
            </div >

            <div className="flex items-center gap-2">
              <button
                className="flex-1 py-2 rounded-lg border border-white/10 text-[#A1A1A1] text-xs hover:bg-white/5 hover:text-white transition-colors"
                onClick={() => upsertProgress({ meals_logged: Math.max(0, (progress.meals_logged || 0) - 1) })}
              >
                -1
              </button>
              <button
                className="flex-[2] py-2 rounded-lg bg-[#E53935]/10 border border-[#E53935]/30 text-[#E53935] text-xs font-bold hover:bg-[#E53935]/20 transition-colors"
                onClick={() => upsertProgress({ meals_logged: Math.min(targetMeals, (progress.meals_logged || 0) + 1) })}
              >
                +1 Refeição
              </button>
            </div>
            {goalCompletedMessage.meals && (
              <div className="mt-3 p-2 rounded-lg bg-[#E53935]/10 border border-[#E53935]/20 text-[#E53935] text-xs font-medium flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3" />
                {goalCompletedMessage.meals}
              </div>
            )}
          </div >

          {/* Sono */}
          < div className="bg-[#141414] rounded-[18px] p-5 border shadow-lg transition-all duration-300" style={{ borderColor: glowSleep ? ACCENT_RED : BORDER_COLOR }
          }>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(229, 57, 53, 0.1)' }}>
                  <Moon className="w-5 h-5 text-[#E53935]" />
                </div>
                <div>
                  <div className="text-white font-bold text-sm">Recuperação</div>
                  <div className="text-[#A1A1A1] text-xs">Meta: {targetSleepH}h</div>
                  <div className="text-[#E53935] text-[10px] mt-0.5">+5 XP ao atingir meta</div>
                </div>
              </div>
              <div className="text-right">
                <span className="text-white font-bold text-lg">{progress.sleep_hours || 0}</span>
                <span className="text-[#A1A1A1] text-xs">h</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                className="flex-1 py-2 rounded-lg border border-white/10 text-[#A1A1A1] text-xs hover:bg-white/5 hover:text-white transition-colors"
                onClick={() => upsertProgress({ sleep_hours: Math.max(0, (progress.sleep_hours || 0) - 0.5) })}
              >
                -30min
              </button>
              <button
                className="flex-[2] py-2 rounded-lg bg-[#E53935]/10 border border-[#E53935]/30 text-[#E53935] text-xs font-bold hover:bg-[#E53935]/20 transition-colors"
                onClick={() => upsertProgress({ sleep_hours: Math.min(12, (progress.sleep_hours || 0) + 0.5) })}
              >
                +30min
              </button>
            </div>
            {goalCompletedMessage.sleep && (
              <div className="mt-3 p-2 rounded-lg bg-[#E53935]/10 border border-[#E53935]/20 text-[#E53935] text-xs font-medium flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3" />
                {goalCompletedMessage.sleep}
              </div>
            )}
          </div >

          {/* Treino */}
          < div className="bg-[#141414] rounded-[18px] p-5 border shadow-lg transition-all duration-300" style={{ borderColor: glowWorkout ? ACCENT_RED : BORDER_COLOR }}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(229, 57, 53, 0.1)' }}>
                  <Dumbbell className="w-5 h-5 text-[#E53935]" />
                </div>
                <div>
                  <div className="text-white font-bold text-sm">Treino</div>
                  <div className="text-[#A1A1A1] text-xs">Semana: {trainingDaysWeek}x</div>
                  <div className="text-[#E53935] text-[10px] mt-0.5">+10 XP ao concluir</div>
                </div>
              </div>
              <div className="text-right">
                {progress.workout_completed ? (
                  <span className="text-[#E53935] font-bold text-xs bg-[#E53935]/10 px-2 py-1 rounded-md border border-[#E53935]/20">CONCLUÍDO</span>
                ) : (
                  <span className="text-[#A1A1A1] text-xs">Pendente</span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                className={`flex-1 py-3 rounded-lg border transition-all flex items-center justify-center gap-2 text-sm font-medium ${progress.workout_completed ? 'bg-[#E53935] border-[#E53935] text-white' : 'bg-transparent border-white/10 text-[#A1A1A1] hover:text-white hover:border-white/30'}`}
                onClick={async () => {
                  try {
                    console.log('Marcando treino como:', !progress.workout_completed);
                    await upsertProgress({ workout_completed: !progress.workout_completed });
                    console.log('Treino atualizado com sucesso');
                  } catch (error) {
                    console.error('Erro ao marcar treino:', error);
                    alert('Erro ao salvar progresso do treino. Tente novamente.');
                  }
                }}
              >
                {progress.workout_completed ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                {progress.workout_completed ? 'Treino Realizado' : 'Marcar como Feito'}
              </button>
            </div>
            {goalCompletedMessage.workout && (
              <div className="mt-3 p-2 rounded-lg bg-[#E53935]/10 border border-[#E53935]/20 text-[#E53935] text-xs font-medium flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3" />
                {goalCompletedMessage.workout}
              </div>
            )}

            {
              !progress.workout_completed && (
                <button
                  type="button"
                  className="w-full mt-3 text-[#E53935] text-xs hover:underline text-center"
                  onClick={() => onGoWorkouts?.()}
                >
                  Ir para aba de Treinos
                </button>
              )
            }
          </div >
        </div >

        {/* Metas personalizadas */}
        {
          customGoals.length > 0 && (
            <div className="mt-8 space-y-4">
              <h3 className="text-white font-bold text-lg px-1">Metas Pessoais</h3>
              {customGoals
                .slice()
                .sort((a, b) => b.createdAt - a.createdAt)
                .map((g) => {
                  return (
                    <div key={g.id} className="bg-[#141414] rounded-[18px] p-5 border shadow-lg group" style={{ borderColor: g.completed ? 'rgba(229, 57, 53, 0.3)' : BORDER_COLOR }}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(229, 57, 53, 0.1)' }}>
                            <Target className="w-5 h-5 text-[#E53935]" />
                          </div>
                          <div>
                            <div className={`font-bold text-sm ${g.completed ? 'text-[#E53935]' : 'text-white'}`}>{g.name}</div>
                            <div className="text-[#A1A1A1] text-xs opacity-80">{g.frequency === 'daily' ? 'Diária' : g.frequency === 'weekly' ? 'Semanal' : 'Mensal'}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button className="p-2 text-[#A1A1A1] hover:text-white transition-colors" onClick={() => startEditCustom(g)}>
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-[#A1A1A1] hover:text-[#E53935] transition-colors" onClick={() => handleDeleteCustom(g.id)}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {g.description && (
                        <div className="text-[#A1A1A1] text-xs mb-4 pl-1">{g.description}</div>
                      )}

                      <button
                        onClick={() => handleToggleCustomCompleted(g.id, !g.completed)}
                        className={`w-full py-3 rounded-lg border transition-all flex items-center justify-center gap-2 text-sm font-medium ${g.completed ? 'bg-[#E53935]/10 border-[#E53935]/30 text-[#E53935]' : 'bg-transparent border-white/10 text-[#A1A1A1] hover:text-white hover:border-white/30'}`}
                      >
                        {g.completed ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                        {g.completed ? 'Concluído' : 'Marcar Conclusão'}
                      </button>

                      {/* Edit inline */}
                      {editingGoalId === g.id && (
                        <div className="mt-4 bg-[#0D0D0D] border rounded-xl p-4" style={{ borderColor: BORDER_COLOR }}>
                          <div className="space-y-3">
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              placeholder="Nome da meta"
                              className="w-full bg-[#1A1A1A] text-white text-sm rounded-lg border px-3 py-3 focus:outline-none focus:border-[#E53935]"
                              style={{ borderColor: BORDER_COLOR }}
                            />
                            <input
                              type="text"
                              value={editDesc}
                              onChange={(e) => setEditDesc(e.target.value)}
                              placeholder="Descrição (opcional)"
                              className="w-full bg-[#1A1A1A] text-white text-sm rounded-lg border px-3 py-3 focus:outline-none focus:border-[#E53935]"
                              style={{ borderColor: BORDER_COLOR }}
                            />
                            <select
                              value={editFreq}
                              onChange={(e) => setEditFreq(e.target.value as CustomFreq)}
                              className="w-full bg-[#1A1A1A] text-white text-sm rounded-lg border px-3 py-3 focus:outline-none focus:border-[#E53935]"
                              style={{ borderColor: BORDER_COLOR }}
                            >
                              <option value="daily">Diária</option>
                              <option value="weekly">Semanal</option>
                              <option value="monthly">Mensal</option>
                            </select>
                            <div className="flex gap-2 pt-2">
                              <button
                                onClick={saveEditCustom}
                                className="flex-1 py-2 bg-[#E53935] text-white rounded-lg text-sm font-bold"
                              >
                                Salvar
                              </button>
                              <button
                                onClick={() => setEditingGoalId(null)}
                                className="flex-1 py-2 border border-white/10 text-[#A1A1A1] rounded-lg text-sm"
                              >
                                Cancelar
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )
        }

        {/* Dica do Dia */}
        <div className="bg-[#141414] rounded-[18px] p-5 border mt-8 shadow-lg" style={{ borderColor: BORDER_COLOR }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(229, 57, 53, 0.1)' }}>
              <Lightbulb className="w-5 h-5 text-[#E53935]" />
            </div>
            <div className="text-white font-bold text-sm">Dica do Dia</div>
          </div>
          <p className="text-[#A1A1A1] text-sm leading-relaxed">{currentTip}</p>
        </div>

        {/* Botão de nova meta */}
        <div className="mt-6">
          {!showAddForm ? (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full py-4 rounded-xl border border-dashed border-white/20 text-[#A1A1A1] text-sm hover:text-white hover:border-white/40 transition-all flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" /> Adicionar nova meta
            </button>
          ) : (
            <div className="bg-[#141414] border rounded-[18px] p-5 shadow-lg" style={{ borderColor: BORDER_COLOR }}>
              <h3 className="text-white font-bold text-sm mb-4">Nova Meta</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  value={newGoalName}
                  onChange={(e) => setNewGoalName(e.target.value)}
                  placeholder="Ex: Beber 3L de água"
                  className="w-full bg-[#1A1A1A] text-white text-sm rounded-xl border px-4 py-3 focus:outline-none focus:border-[#E53935]"
                  style={{ borderColor: BORDER_COLOR }}
                />
                <input
                  type="text"
                  value={newGoalDesc}
                  onChange={(e) => setNewGoalDesc(e.target.value)}
                  placeholder="Descrição (opcional)"
                  className="w-full bg-[#1A1A1A] text-white text-sm rounded-xl border px-4 py-3 focus:outline-none focus:border-[#E53935]"
                  style={{ borderColor: BORDER_COLOR }}
                />
                <select
                  value={newGoalFreq}
                  onChange={(e) => setNewGoalFreq(e.target.value as CustomFreq)}
                  className="w-full bg-[#1A1A1A] text-white text-sm rounded-xl border px-4 py-3 focus:outline-none focus:border-[#E53935]"
                  style={{ borderColor: BORDER_COLOR }}
                >
                  <option value="daily">Diária</option>
                  <option value="weekly">Semanal</option>
                  <option value="monthly">Mensal</option>
                </select>
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={handleAddCustom}
                    className="flex-1 py-3 bg-[#E53935] text-white rounded-xl text-sm font-bold shadow-lg shadow-red-900/20"
                  >
                    Salvar Meta
                  </button>
                  <button
                    onClick={() => setShowAddForm(false)}
                    className="flex-1 py-3 border border-white/10 text-[#A1A1A1] rounded-xl text-sm hover:bg-white/5"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {
          snack && (
            <div className="fixed bottom-24 left-0 right-0 flex justify-center z-[70] px-4">
              <div className="max-w-md w-full rounded-xl bg-[#141414] border shadow-2xl px-4 py-3 text-sm text-white flex items-center gap-3" style={{ borderColor: BORDER_COLOR, boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                <div className="p-1 rounded-full bg-[#E53935]">
                  <CheckCircle2 className="w-3 h-3 text-white" />
                </div>
                <span className="font-medium">{snack}</span>
              </div>
            </div>
          )
        }
      </div >
    </div >
  );
}